import {FormDataSchema} from "../form/FormDataSchema.model";
import {DataType} from "./DataType.model";
import {GridIdKeyConstant, GridLinkIdKeyConstant} from "../component_element/manifest_constant/Grid.constant";
import {GridContainerRole, GridProps} from "../form/Grid.model";
import {ComponentProperties} from "../component/Component.model";
import {FormContentConditionName} from "../form/FormContent.model";

/**
 * 서브젝트 출처
 * 데이터를 전송한 객체가 같은 데이터를 수신했을 경우, 그 출처를 확인하기 위한 용도
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export interface BaseSubject {
    fromUniqueId?: string;
    elementId?: string;
    checkConflict?: boolean;
}
////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 컴퍼넌트 생명주기 종류
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum ComponentLifeCycleSubjectType {
    /** 컴퍼넌트 초기화 완료 */
    ININDONE,
    /** 컴퍼넌트가 자식 앨리먼트를 가질 수 있는 상태 */
    ENABLEAPPENDCHILD,
    /** 컴퍼넌트가 여러개의 container 을 가졌을 경우 특정 container 가 자식 앨리먼트를 가질 수 있는 상태 */
    ENABLEAPPENDCHILDBYCONTAINER,
    /** 컴퍼넌트 제거 요청 */
    DESTROY,
    /** 컴퍼넌트 제거 완료 */
    DESTROYDONE,
    /** 컴퍼넌트 포커스 요청 */
    FOCUS,
    /** 컴퍼넌트 레이블 FOR 알리아스 */
    ALIASID
}

/**
 * 컴퍼넌트 생명주기 통보 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface ComponentLifeCycleSubject extends BaseSubject {
    /** 같은 폼, 또는 같은 페이지가 한 웹페이지에 여러번 삽입된 경우 구별자 */
    globalIndex: string;
    /** initDone or destroyDone */
    lifeCycle: ComponentLifeCycleSubjectType;
    /** 자식 앨리먼트를 지닐 수 있는 컨테이터 키 */
    container?: string;
    /** 자식 앨리먼트가 참조할 수 있는 부모 돔 */
    dom?: Array<HTMLElement>;
    /** 윈도우 이름 */
    name?: string;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 폼 데이터 스키마 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormDataSchemaSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId?: string;
    /** initDone or destroyDone */
    formDataSchema: FormDataSchema;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 폼 데이터 서브젝트 (폼 컴퍼넌트 -> 서비스)
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormDataSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId?: string;
    /** 폼 데이터 */
    fieldId: string;
    /** 데이터 타입 */
    dataType: DataType;
    /** 밸류 */
    value: any;
    /** 그리드 정보 */
    grid?: GridProps;
}

/**
 * 폼 데이터 서브젝트 (서비스 - 폼 컴퍼넌트)
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormDataChangeSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId?: string;
    /** 폼 데이터 */
    fieldId: string;
    /** 밸류 */
    value: any;
    /** 그리드 정보 */
    grid?: GridProps;
}

/**
 * 폼 스테이트 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormStateSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId?: string;
    /** 스테이트를 발송할때 테그를 붙여주면 스테이트 챈지 이벤트 발생시 추가하여 알려줌 */
    tag?: any;
    /** 스테이트 */
    state: any;
    /** 이벤트 */
    event?: any;
    /** 컴퍼넌트 인스턴스 프로퍼티 */
    componentProperties?: ComponentProperties;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 폼 컨텐츠 타입
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum FormContentSubjectType {
    CHANGEPROPERTY = "changeProperty",
    CHANGECONDITION = "changeCondition",
    CHANGEVALIDATION = "changeValidation",
    DROPELEMNTORSCHEMA = "dropElementOrSchema",
    ADDCOMPONENT = "addComponent",
    DELETECOMPONENT = "deleteComponent",
    OPENCOMPONENTSELECTOR = "openComponentSelector",
    SELECTEDCOMPONENT = "selectedComponent",
    CHANGEDLAYOUTPROPERTY = "changedLayoutProperty",
    CHANGEDPROPERTY = "changedProperty",
    CHANGEDDATASCHEMAFIELD = "changedDataSchemaField",
    CHANGEDCOMPONENTFIELD = "changedComponentField",
    CHANGEDPARENTID = "changedParentId",
    RELOADEMBEDCONTENTS = "reloadEmbedContents",
    PAGECHILDNODES = "pageChildNodes",
    // MODALOPENMODE = "modalOpenMode",
    GLOBALCONDITIONREADONLY = "globalConditionReadonly",
    GLOBALCONDITIONPRINTABLE = "globalConditionPrintable",
    /** 디자인 타임에 컴퍼넌트별로 아이콘 표시(컨디션 있음, 이벤트 있음 등) */
    SETCONDITIONICON = "setConditionIcon",
    /** 디자인 패널 삭제 */
    REMOVEDESIGNER = "removeDesigner",
    /** 삭제된 폼 / 블럭 */
    REMOVEPERMANENT = "removePermanent"
}

/**
 * 폼 컨텐츠 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormContentSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId?: string;
    /** type */
    type: FormContentSubjectType;
    /** 값 */
    value: any;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 프레임간 통신
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum FrameSubjectType {
    CLICK = "click"
}

/**
 * 프레임간 통신 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FrameSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId?: string;
    /** 폼 아이디 */
    formId?: string;
    /** type */
    type: FrameSubjectType;
    /** frame */
    frame: any;
    /** 값 */
    value?: any;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 그리드
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum GridSubjectType {
    /** 디자인 타임에서 본인의 위치가 변경되거나 부모의 위치가 변경 되었을 때 */
    MOVE = "move",
    /** 콜 액션 */
    CALLACTION = "callAction",
    /** 컨테이너 내 데이터 바인드 */
    DATABIND = "dataBind",
    /** 리피티드 컨데이너 내 PK 변화 수용 */
    CHANGEPK = "changePk",
    /** 다른 리스트 리로드 요청 */
    REQRELOAD = "reqReload",
    /** 리트리브 데이터 업데이트 */
    RETRIEVBIND = "retrieveBind",
    /** 리트리브 이미 있는 데이터 */
    RETRIEVEPK = "retrievEpk",
    /** 테이블 내 파일 리스트 */
    FILELIST = "fileList",
    /** 페이지 리로드 요청(외부) */
    RELOAD = "reload"
}

/**
 * 그리드 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface GridSubject extends BaseSubject {
    /** 타입 */
    type: GridSubjectType;
    /** 특정 어플리케이션(프로젝트) */
    applicationId?: string;
    /** 특정 폼 */
    formId?: string;
    /** 그리드 아이디 */
    [GridIdKeyConstant]?: string;
    /** 그리드 링크 아이디 */
    [GridLinkIdKeyConstant]?: string;
    /** primary key */
    pk?: string;
    /** 컨테이너 관련 값 */
    containerElementId?: string;
    containerRole?: GridContainerRole;
    containerPk?: string;
    retrieve?: boolean;
    /** 타입에 대한 기타 값 */
    value?: any;
    /** 특수 목적의 유니크 아이디: 예를 들어 목록이 다수일 때 검색을 하면 다수의 목록이 갱신되고 서로 리로드 요청을 할 수 있으므로 같은 유니크 아이디로는 한 번만 되도록 하는 등의 목적 */
    uniqueId?: string;
    /** 컴퍼넌트가 여럿 들어가 있을 경우 해당 컴퍼넌트를 특정짓기 위함 **/
    globalIndex?: string;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 노티피케이션 타입
 * 예) i18n 로딩 완료, 앱 컨피그 로딩 완료 등
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum NotificationSubjectType {
    /** 플러그인 로딩 완료 */
    PLUGINDONE = "pluginDone",
    /** 유저 애플리케이션 컨피그 로딩 완료 */
    USERAPPCONFIGDONE = "userAppConfigDone",
    /** I18n 로딩 요청 */
    I18NREAD = "i18nRead",
    /** I18n 로딩 완료 또는 재로딩*/
    I18NDONE = "i18nDone",
    /** PROJECT 정보 로딩 요청 */
    PROJECTREAD = "projectRead",
    /** PROJECT 로딩 완료 또는 재로딩*/
    PROJECTDONE = "projectDone",
    /** 프로젝트 데이터 스키마 요청 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    PROJECTSCHEMAREAD = "projectSchemaRead",
    /** 데이터모델러에서 프로젝트 데이터 스키마 요청 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    PROJECTSCHEMAREADFORDATAMODELER = "projectSchemaReadForDataModeler",
    /** 프로젝트 데이터 스키마 가져오기 완료 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    PROJECTSCHEMADONE = "projectSchemaDone",
    /** 프로젝트 데이터 스키마를 클라이언트의 저장소에서 로딩한 경우 발생(데이터모델러) */
    PROJECTSCHEMADONEFROMSAVEDDATA = "ProjectSchemaDoneFromSavedData",
    /** 프로젝트 데이터 스키마 변경(데이터모델러에서 스키마 변경 시 발생) */
    PROJECTSCHEMACHANGE = "projectSchemaChange",
    /** 페이지 요청 */
    PAGEREAD = "pageRead",
    /** 페이지 데이터 가져오기 완료 */
    PAGEDONE = "pageDone",
    /** 폼 요청 */
    FORMREAD = "formRead",
    /** 폼 가져오기 완료 */
    FORMDONE = "formDone",
    /** 폼 / 페이지 셀렉터에서 무조건 다시 가져오기 */
    FORMANDPAGERELOAD = "formAndPageReload",
    /** 폼 데이터 요청 */
    FORMDATAREAD = "formDataRead",
    /** 폼 데이터 가져오기 완료 */
    FORMDATADONE = "formDataDone",
    /** 폼 렌더링 데이터 요청 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    FORMRENDERREAD = "formRenderRead",
    /** 폼 렌더링 데이터 가져오기 완료 /  폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    FORMRENDERDONE = "formRenderDone",
    /** 폼 데이터 스키마 요청 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    FORMDATASCHEMAREAD = "formDataSchemaRead",
    /** 폼 데이터 스키마 가져오기 완료 / 폼 최초 로딩 시 서버에 저장된 마지막 데이터 */
    FORMDATASCHEMADONE = "formDataSchemaDone",
    /** 폼 스테이트 변경 통보 / 폼 스테이트에 관심있는 객체가 구독 */
    FORMSTATECHANGE = "formStateChange",
    /** 폼 스테이트 초기화 요청 */
    FORMSTATEINIT = "formStateInit",
    /** 폼 스테이트 초기화 완료 */
    FORMSTATEDONE = "formStateDone",
    /** 폼 스테이트 변경 통보 / 차이없음 / 폼 스테이트에 관심있는 객체가 구독 */
    FORMSTATENONECHANGE = "formStateNoneChange",
    /** 폼 변경 사항 생김*/
    FORMHASSOMETHINGTOSAVE = "formHasSomeThingToSave",
    /** 폼 스키마 만들기 */
    NEEDTOCALCULATEFORMDATASCHEMA = "needToCalculateFormDataSchema",
    /** 폼 릴레이션쉽 체크 완료 */
    FORMRELATIONSHIPDONE = "formRelationshipDone",
    /** 커스텀 컴퍼넌트 로드 완료 */
    CUSTOMCOMPONENTDONE = "customComponentDone",
    /**
     * Dock and Auto Fill Checking
     * 새로운 Dcuk 또는 Auto Fill panel 이 추가/제거되었을 경우 통보
     * Dock 의 크기가 변경 되었을 때 통보 등
     */
    DOCKANDAUTOFILLCHECKING = "dockAndAutoFillChecking",
    /**
     * 디자인 타임에서 컴퍼넌트가 선택(변경/삭제) 되었을 때 통보
     */
    SELECTEDCOMPONENT = "selectedComponent",
    /** Work */
    WORKSPACEREAD = "workspaceRead",
    WORKSPACEDONE = "workspaceDone",
    /** LookUp Type */
    LOOKUPTYPEREAD = "lookupTypeRead",
    LOOKUPTYPEDONE = "lookupTypeDone",
    /** Raw Data Schema */
    RAWDATASCHEMAREAD = "RawDataSchemaRead",
    RAWDATASCHEMADONE = "RawDataSchemaDone",
    /** Revert Form */
    REVERTFORM = "revertForm",
    /**
     * Validation Request / Response
     * Validation marker 용도
     * */
    VALIDATIONREQUEST = "validationRequest",
    VALIDATIONRESPONSE = "validationResponse",
    /** reload Extend Css and JavaScript */
    RELOADEXTENDCSSANDJAVASCRIPT = "reloadExtendCssAndJavaScript",
    /** render grid repeated container */
    GRIDREPEATCONTAINERRENDERING = "gridRepeatContainerRendering"
}

/**
 * 노티피케이션 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface NotificationSubject extends BaseSubject {
    /** 타입 */
    type: NotificationSubjectType;
    /** 특정 어플리케이션(프로젝트) */
    applicationId?: string;
    /** 특정 폼 */
    formId?: string;
    /** 테그가 있으면 발송 */
    tag?: any;
    /** 타입에 대한 값 */
    value?: any;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 소켓 메세지 통보
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum SocketSubjectType {
    RELOADPROJECTSCHEMASTART = "reloadProjectSchemaStart",
    RELOADPROJECTSCHEMADONE = "reloadProjectSchemaDone"
}

/**
 * 프레임간 통신 서브젝트
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface SocketSubject extends BaseSubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** type */
    type: SocketSubjectType;
    /** frame */
    message?: any;
    /**  tag */
    tag?: any;
}

////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 부모 컴퍼넌트 Display Inheritance
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface DisplaySubject {
    /** 프로젝트 아이디 */
    projectId: string;
    /** 폼 아이디 */
    formId: string;
    /** 부모 엘리먼트 아이디 */
    pDisplayId?: string;
    /** 엘리먼트 아이디 */
    elementId?: string;
    /** 부모 디스플레이 모드 */
    display?: FormContentConditionName;
    /** 디자인타임에서 드래그 앤 드롭으로 위치가 변경되었을 당시 소스 엘리먼트가 이미 디스플레이 속성을 가질 경우 */
    hasDisplay?: boolean;
    /** 글로벌 인덱스 아이디 */
    globalIndex?: string;
    /** Grid 안 일때 pk */
    pk?: string;
}
